import * as Sentry from "@sentry/nextjs";
import { useCallback } from "react";
import { useAuth } from "react-oidc-context";

import { PublicRoute } from "@/enums/route";
import { resetAnalytics } from "@/lib/analytics";
import { clearBootHint } from "@/lib/bootHint";
import { logError } from "@/lib/logError";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { runWithCleanup } from "@/utils/runWithCleanup";

/** Async, so a synchronous throw from `removeUser` arrives as a rejection. */
const dropLocalTokens = async (removeUser: () => Promise<void>) => {
  await removeUser();
};

export interface LogoutOptions {
  /**
   * Path to land on after the Keycloak RP-initiated sign-out completes.
   * Defaults to the public logged-out page. Inactivity logout passes
   * `Route.Dashboard` ("/") so the auth gate forces a real re-login.
   */
  redirectTo?: string;
  /**
   * Why the logout happened. Does NOT change the teardown — both paths do a
   * full sign-out — it's a hook for callers/analytics to distinguish the
   * manual "Log out" click from an inactivity timeout.
   */
  reason?: "user" | "inactivity";
}

/**
 * Join an origin and a route path into a single absolute URL with exactly one
 * separating slash. The previous template (`${origin}/${PublicRoute.LoggedOut}`)
 * produced a tolerated double slash (`origin//logged-out`); this normalises it,
 * and lets `redirectTo: "/"` resolve cleanly to the origin root.
 */
export function composePostLogoutRedirectUri(
  origin: string,
  redirectTo: string
): string {
  const trimmedOrigin = origin.replace(/\/+$/, "");
  const normalisedPath = redirectTo.startsWith("/")
    ? redirectTo
    : `/${redirectTo}`;
  return `${trimmedOrigin}${normalisedPath}`;
}

/**
 * Single logout path for the app. Manual logout (nav-user) calls it with no
 * args → `/logged-out`; inactivity logout calls it with `redirectTo: "/"` so
 * the auth gate forces re-authentication.
 *
 * Always: clears the pending-items-badge dismissal and the boot prefetch hint,
 * resets Mixpanel identity, clears the Sentry user, and reports a silent failure if `signoutRedirect`
 * resolves `null` (the library swallows end-session failures — see below).
 */
export function useLogout() {
  const auth = useAuth();

  return useCallback(
    (options?: LogoutOptions) => {
      const redirectTo = options?.redirectTo ?? PublicRoute.LoggedOut;

      if (typeof window !== "undefined") {
        // Clear pending items badge dismissal on logout (moved here from
        // nav-user so every logout path gets it).
        window.sessionStorage.removeItem("pending-items-badge-dismissed");
        // The next sign-in on this device must not prefetch this user's last
        // account (the hint is sub-bound anyway; this is defence in depth).
        clearBootHint();
      }

      // Reset Mixpanel to generate a new anonymous ID for the next user.
      resetAnalytics();
      Sentry.setUser(null);

      const postLogoutRedirectUri = composePostLogoutRedirectUri(
        window.location.origin,
        redirectTo
      );

      // react-oidc-context wraps signoutRedirect in its own try/catch and
      // RESOLVES with null instead of rejecting when the underlying call
      // throws (end-session metadata fetch failure, redirect construction
      // error). A void-ed promise therefore never surfaces the failure —
      // the user clicks "Log out", nothing happens, and on a shared device
      // they may walk away believing the session is closed.
      void auth
        .signoutRedirect({ post_logout_redirect_uri: postLogoutRedirectUri })
        .then(async (result: unknown) => {
          // Typed Promise<void>, but the library's navigator wrapper actually
          // resolves null on failure. On success the browser navigates away
          // before this runs; reaching here with null means the redirect
          // never started.
          if (result !== null) return;
          reportSilentFailure({
            message: "[Auth] signoutRedirect failed — user not logged out",
            fingerprint: "auth-signout-failed",
          });
          // Keycloak end-session could not start (e.g. Keycloak unreachable).
          // Fall back to a LOCAL teardown so the logout still completes on
          // this device: drop the stored tokens, then hard-navigate to the
          // redirect target. Security-critical for the inactivity path — the
          // user has walked away, and "we reported it to Sentry" must not be
          // the only outcome. The Keycloak SSO cookie survives (nothing we
          // can do while it's unreachable), but without the local refresh
          // token this app is signed out. runWithCleanup, not try/finally,
          // keeps the React Compiler lint rules on for this hook; the log
          // lands before the navigation.
          await runWithCleanup(
            () =>
              dropLocalTokens(() => auth.removeUser()).catch(
                (error: unknown) => {
                  logError(
                    "[useLogout] Local token teardown failed after signoutRedirect failure",
                    error
                  );
                }
              ),
            () => {
              if (typeof window !== "undefined") {
                window.location.assign(redirectTo);
              }
            }
          );
        });
    },
    [auth]
  );
}
